// Plan-based access control
// Each plan unlocks a set of features. Higher plans include all lower-plan features.

export type PlanFeature =
  | 'dashboard'
  | 'contacts'
  | 'campaigns'
  | 'templates'
  | 'reports'
  | 'groups'
  | 'settings_whatsapp'
  | 'settings_general'
  | 'settings_users'
  | 'logs'
  | 'invoices'
  | 'my_usage'
  | 'setup_fee'
  | 'credit_landing'
  | 'profile'
  | 'subscription'
  | 'integrations'
  | 'admin_panel';

// Which plan unlocks which features (cumulative — each tier includes previous tiers)
const PLAN_FEATURES: Record<string, PlanFeature[]> = {
  trial: [
    'dashboard',
    'contacts',
    'campaigns',
    'templates',
    'groups',
    'reports',
    'settings_whatsapp',
    'settings_general',
    'logs',
    'invoices',
    'my_usage',
    'integrations',
    'profile',
    'subscription',
    'credit_landing',
  ],
  starter: [
    'dashboard',
    'contacts',
    'campaigns',
    'templates',
    'groups',
    'settings_whatsapp',
    'settings_general',
    'integrations',
    'my_usage',
    'setup_fee',
    'profile',
    'subscription',
    'credit_landing',
  ],
  professional: [
    'dashboard',
    'contacts',
    'campaigns',
    'templates',
    'reports',
    'groups',
    'settings_whatsapp',
    'settings_general',
    'settings_users',
    'logs',
    'invoices',
    'integrations',
    'my_usage',
    'setup_fee',
    'profile',
    'subscription',
    'credit_landing',
  ],
  business: [
    'dashboard',
    'contacts',
    'campaigns',
    'templates',
    'reports',
    'groups',
    'settings_whatsapp',
    'settings_general',
    'settings_users',
    'logs',
    'invoices',
    'integrations',
    'my_usage',
    'setup_fee',
    'profile',
    'subscription',
    'credit_landing',
  ],
  enterprise: [
    'dashboard',
    'contacts',
    'campaigns',
    'templates',
    'reports',
    'groups',
    'settings_whatsapp',
    'settings_general',
    'settings_users',
    'logs',
    'invoices',
    'integrations',
    'my_usage',
    'setup_fee',
    'profile',
    'subscription',
    'credit_landing',
  ],
};

// Plan display order (for sorting)
export const PLAN_ORDER: Record<string, number> = {
  trial: 0,
  starter: 1,
  professional: 2,
  business: 3,
  enterprise: 4,
};

// Plan feature descriptions for UI
export const PLAN_FEATURE_LABELS: Record<PlanFeature, string> = {
  dashboard: 'Dashboard',
  contacts: 'Contatos',
  campaigns: 'Campanhas',
  templates: 'Templates',
  reports: 'Relatórios',
  groups: 'Grupos',
  settings_whatsapp: 'Config. WhatsApp',
  settings_general: 'Config. Gerais',
  settings_users: 'Config. Usuários',
  logs: 'Logs de Envio',
  invoices: 'Faturas',
  my_usage: 'Meu Uso',
  setup_fee: 'Implantação',
  credit_landing: 'Créditos',
  profile: 'Perfil',
  subscription: 'Assinatura',
  integrations: 'Integrações',
  admin_panel: 'Painel Admin',
};

// Trial duration in days
export const TRIAL_DURATION_DAYS = 7;

// Trial plan limits
export const TRIAL_LIMITS = {
  messageLimit: 500,
  users: 1,
  whatsapps: 1,
};

/**
 * Check if a given plan has access to a specific feature.
 * Admin users always have access to everything.
 */
export function planHasFeature(plan: string | undefined, feature: PlanFeature): boolean {
  if (!plan) return false;
  const features = PLAN_FEATURES[plan];
  if (!features) return false;
  return features.includes(feature);
}

/**
 * Get all features for a plan.
 * Admin users get all features.
 */
export function getPlanFeatures(plan: string | undefined): PlanFeature[] {
  if (!plan) return [];
  return PLAN_FEATURES[plan] || [];
}

/**
 * Calculate trial remaining days.
 * Returns negative if expired, 0 if last day, positive if days remaining.
 * Returns null if user is not on trial plan.
 */
export function getTrialDaysRemaining(plan: string | undefined, createdAt: string | undefined): number | null {
  if (plan !== 'trial' || !createdAt) return null;
  const trialStart = new Date(createdAt);
  const trialEnd = new Date(trialStart.getTime() + TRIAL_DURATION_DAYS * 24 * 60 * 60 * 1000);
  const now = new Date();
  const remaining = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return remaining;
}

/**
 * Check if trial has expired.
 */
export function isTrialExpired(plan: string | undefined, createdAt: string | undefined): boolean {
  const remaining = getTrialDaysRemaining(plan, createdAt);
  if (remaining === null) return false;
  return remaining <= 0;
}